import { Check, Hash, Search } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, EmptyState, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAvailableNumbers, useReserveNumber, useTenants } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { useDebounced } from "@/lib/use-debounced";

/**
 * Request a number from the pool.
 *
 * Only what is available is offered — the pool's other rows (held, assigned elsewhere) are
 * not this tenant's business, and the server hides them too. The claim is atomic on the
 * server, so the number leaves the list for everyone the moment it is requested; a 409 here
 * means somebody else got there first, and the list refreshes to show what is left.
 */
export function RequestNumberDialog({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { user: actor } = useAuth();
  const toast = useToast();
  const reserve = useReserveNumber();
  const [search, setSearch] = React.useState("");
  const [areaCode, setAreaCode] = React.useState("");
  const [numberType, setNumberType] = React.useState("all");
  const [selected, setSelected] = React.useState<number | null>(null);
  const [note, setNote] = React.useState("");
  const [tenantId, setTenantId] = React.useState<number | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);
  const debounced = useDebounced(search, 200);
  const debouncedArea = useDebounced(areaCode, 200);

  const isSuperadmin = actor?.role === "superadmin";
  const { data: tenants } = useTenants({ enabled: open && isSuperadmin });
  const { data: available, isPending, refetch } = useAvailableNumbers(
    {
      q: debounced || undefined,
      area_code: debouncedArea || undefined,
      number_type: numberType === "all" ? undefined : numberType,
    },
    { enabled: open },
  );

  React.useEffect(() => {
    if (!open) return;
    setSelected(null);
    setNote("");
    setSearch("");
    setAreaCode("");
    setNumberType("all");
    setFormError(null);
    setTenantId(actor?.tenant_id ?? (tenants?.length === 1 ? tenants[0]!.id : null));
  }, [open, actor?.tenant_id, tenants]);

  const chosen = available?.find((n) => n.id === selected);
  // The search, area code and type only narrow the list; the request is the number, its note and
  // the tenant, measured against what opening the dialog set.
  const changed =
    selected !== null ||
    note.trim() !== "" ||
    tenantId !== (actor?.tenant_id ?? (tenants?.length === 1 ? tenants[0]!.id : null));

  const submit = async () => {
    if (selected === null) return;
    setFormError(null);
    try {
      const order = await reserve.mutateAsync({
        phone_number_id: selected,
        note: note.trim() || null,
        ...(isSuperadmin && tenantId !== null ? { tenant_id: tenantId } : {}),
      });
      toast.success("Number requested", `${order.number} is awaiting approval`);
      onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setSelected(null);
        void refetch();
      }
      setFormError(e instanceof ApiError ? e.message : "Could not request the number.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>Request a number</DialogTitle>
          <DialogDescription>
            Pick a number from the pool. A superadmin approves it, and it then appears in your
            numbers ready to route.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {formError && <Callout tone="danger">{formError}</Callout>}

          <div className="flex flex-wrap gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search digits"
              leading={<Search />}
              className="min-w-40 flex-1"
              aria-label="Search available numbers"
            />
            <Input
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
              placeholder="Area code"
              className="w-28 font-mono"
              aria-label="Area code"
            />
            <Select value={numberType} onValueChange={setNumberType}>
              <SelectTrigger className="w-32" aria-label="Number type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any type</SelectItem>
                <SelectItem value="local">Local</SelectItem>
                <SelectItem value="tollfree">Toll-free</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isPending ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (available ?? []).length === 0 ? (
            <EmptyState
              icon={Hash}
              title="Nothing available"
              description="The pool has no free numbers matching this. Ask a superadmin to add some."
              className="py-8"
            />
          ) : (
            <ul className="max-h-64 space-y-1 overflow-y-auto scrollbar-subtle" role="listbox">
              {(available ?? []).map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected === n.id}
                    onClick={() => setSelected(n.id)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-md border px-3 py-2 text-left",
                      "transition-colors",
                      selected === n.id
                        ? "border-accent-8 bg-accent-2"
                        : "border-border hover:bg-surface-hover",
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block font-mono text-sm text-text">{n.number}</span>
                      <span className="block truncate text-xs text-text-muted">
                        {[n.locality, n.region].filter(Boolean).join(", ") || n.notes || "—"}
                      </span>
                    </span>
                    {n.number_type === "tollfree" && (
                      <Badge tone="outline" size="sm">
                        Toll-free
                      </Badge>
                    )}
                    {selected === n.id && <Check className="size-4 shrink-0 text-accent-11" aria-hidden />}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {isSuperadmin && (tenants?.length ?? 0) > 1 && (
            <Field label="For tenant" htmlFor="request-tenant" required>
              <Select
                value={tenantId?.toString() ?? ""}
                onValueChange={(v) => setTenantId(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a tenant" />
                </SelectTrigger>
                <SelectContent>
                  {(tenants ?? []).map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          <Field
            label="What is it for?"
            htmlFor="request-note"
            hint="Shown to the approver, and becomes the number's description."
          >
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Larry H Parker — CA intake line"
              autoComplete="off"
              maxLength={500}
            />
          </Field>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="default"
            loading={reserve.isPending}
            disabled={selected === null || (isSuperadmin && tenantId === null)}
            onClick={() => void submit()}
          >
            {chosen ? `Request ${chosen.number}` : "Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
